import * as React from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import {
  NODE_PARAGRAPH,
  render,
  MARK_LINK,
} from 'storyblok-rich-text-react-renderer';
import StoryBlokLink from './StoryBlokLink';
import LinkResolver from '../../helpers/link-resolver';
import Container from '../Container';
import { getStoryblokAlt } from '../../helpers/common';

const ITEMS_PER_PAGE = 9;

function richText(content) {
  return render(content, {
    markResolvers: { [MARK_LINK]: LinkResolver },
    nodeResolvers: {
      // eslint-disable-next-line react/no-unstable-nested-components
      [NODE_PARAGRAPH]: (children) => (
        <p className="first-of-type:mt-0 last-of-type:mb-0">{children}</p>
      ),
    },
  });
}

function PartnerCard({ partner }) {
  const hasLink = partner?.link?.url || partner?.link?.cached_url;

  return (
    <div
      {...storyblokEditable(partner)}
      className="flex flex-col border-2 border-solid rounded border-latori-green-dark-dark overflow-hidden"
    >
      {/* Logo header — full-width dark area */}
      <div className="p-5">
        <div
          className="flex items-center justify-start p-6 min-h-[90px] bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/assets/images/dark-green-bg.png)' }}
        >
          {partner.icon?.filename ? (
            <span className="fancy-title-img">
              <img
                src={
                    partner.icon.filename.includes('.svg')
                      ? partner.icon.filename
                      : `${partner.icon.filename}/m/filters:format(webp)`
                    }
                alt={getStoryblokAlt(partner.icon, partner.title || '')}
                className="max-h-10 w-auto object-contain"
                loading="lazy"
              />
            </span>
          ) : (
            <span className="text-white font-bold text-2xl">{partner.title}</span>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="flex flex-col flex-1 px-5 py-5 gap-4">
        {partner.description && (
          <div className="text-sm leading-relaxed partners-overview-description">
            {richText(partner.description)}
          </div>
        )}
        {hasLink && (
          <div className="mt-auto pt-2">
            <StoryBlokLink
              link={partner.link}
              className="text-latori-pink font-semibold text-sm hover:underline"
            >
              {partner.link_label || 'Zum Blog'}
            </StoryBlokLink>
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturedPartner({ partner }) {
  const hasLink = partner?.link?.url || partner?.link?.cached_url;

  const imageContent = partner.image?.filename ? (
    <>
      <img
        src={
          partner.image.filename.includes('.svg')
            ? partner.image.filename
            : `${partner.image.filename}/m/filters:format(webp)`
        }
        alt={getStoryblokAlt(partner.image, partner.title || '')}
        loading="lazy"
      />
      {partner.image_label && (
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
          <span className="text-white font-semibold text-lg">{partner.image_label}</span>
        </div>
      )}
    </>
  ) : (
    <div className="w-full h-full absolute inset-0 bg-background-dark flex items-center justify-center p-8">
      {partner.logo?.filename ? (
        <img
          src={
            partner.logo.filename.includes('.svg')
              ? partner.logo.filename
              : `${partner.logo.filename}/m/600x400/filters:format(webp)`
          }
          alt={getStoryblokAlt(partner.logo, partner.title || '')}
          className="max-h-16 w-auto object-contain"
          loading="lazy"
        />
      ) : (
        <span className="text-white text-2xl font-bold">{partner.title}</span>
      )}
    </div>
  );

  return (
    <div
      {...storyblokEditable(partner)}
      className="flex flex-col lg:flex-row overflow-hidden mb-8"
    >
      {/* Image side */}
      <div className="relative lg:w-3/5 min-h-[200px] overflow-hidden border-2 border-solid border-latori-green-dark-dark rounded p-4">
        {hasLink ? (
          <StoryBlokLink link={partner.link} className="block">
            {imageContent}
          </StoryBlokLink>
        ) : imageContent}
      </div>

      {/* Content side */}
      <div className="lg:w-2/5 px-0 md:px-8 py-8 flex flex-col justify-center gap-4">
        {partner.title && (
          hasLink ? (
            <StoryBlokLink link={partner.link} className="hover:underline">
              <h3 className="text-3xl font-bold fancy-title">{partner.title}</h3>
            </StoryBlokLink>
          ) : (
            <h3 className="text-3xl font-bold fancy-title">{partner.title}</h3>
          )
        )}
        {partner.description && (
          <div className="text-sm leading-relaxed">
            {richText(partner.description)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PartnersOverview({ blok }) {
  const [activeCat, setActiveCat] = React.useState('all');
  const [currentPage, setCurrentPage] = React.useState(1);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const partners = blok?.partners || [];

  // Derive unique categories from partner entries
  const categories = [
    'all',
    ...[...new Set(partners.map((p) => p.category).filter(Boolean))],
  ];
  const hasCategoryFilter = categories.length > 1;

  const handleCatChange = (cat) => {
    setActiveCat(cat);
    setCurrentPage(1);
  };

  const filteredPartners = activeCat === 'all'
    ? partners
    : partners.filter((p) => p.category === activeCat);

  const totalPages = Math.ceil(filteredPartners.length / ITEMS_PER_PAGE);
  const paginatedPartners = filteredPartners.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  if (!partners.length) {
    return null;
  }

  return (
    <div {...storyblokEditable(blok)} className="partners-overview mt-10 lg:mt-20">
      <Container>
        {/* Category filter tabs */}
        {hasCategoryFilter && (
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => handleCatChange(cat)}
                className={`px-4 py-1.5 rounded text-sm font-medium transition-colors capitalize ${
                  activeCat === cat
                    ? 'bg-latori-green-dark text-white'
                    : 'text-background-dark border rounded bg-background-light'
                }`}
              >
                {cat === 'all' ? (blok.all_label || 'Alle') : cat}
              </button>
            ))}
          </div>
        )}

        {/* Featured spotlight — always the first item of the current page */}
        {/* {featuredPartner && (
          <FeaturedPartner partner={featuredPartner} />
        )} */}

        {/* Partner grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {paginatedPartners.map((partner) => (
            <PartnerCard key={partner._uid} partner={partner} />
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-6 mt-10">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => { setCurrentPage(page); scrollToTop(); }}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                  currentPage === page
                    ? 'bg-latori-green-dark text-white'
                    : 'text-gray-600 hover:text-latori-green-dark'
                }`}
              >
                {page}
              </button>
            ))}
            {currentPage < totalPages && (
              <button
                type="button"
                onClick={() => { setCurrentPage((p) => p + 1); scrollToTop(); }}
                className="ml-2 text-sm font-medium text-gray-600 hover:text-latori-green-dark"
              >
                NEXT &rsaquo;
              </button>
            )}
          </div>
        )}
      </Container>
    </div>
  );
}
